// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useCallback, useState, useMemo, createContext, useEffect, useRef } from 'react';
import { authModule } from 'authentication/authModule';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Cancellable, useCancellableTask } from 'hooks/useCancellableTask';
import { initLogger } from 'utils/logger';
import { devTestUser, isDevEnvironment } from 'utils/devEnvironment';
import { User } from 'utils/User';
import { clearLocalStorage } from 'utils/localStorage';
import { IApplicationInsights } from '@microsoft/applicationinsights-web';
import { getBotServerAppInsightsConnectionString } from 'utils/appInsightsUtils';
import { BotServerAppInsights } from 'utils/BotServerAppInsights';

declare const CALLING_SDK_VERSION: string;
declare const CHAT_SDK_VERSION: string;
declare const COMMUNICATION_REACT_SDK_VERSION: string;

export type SignedInState = 'NotSignedIn' | 'CheckingState' | 'SignedIn';

export interface SignInContext {
  user: User | undefined;
  signedInState: SignedInState;
  appInsights?: IApplicationInsights;
  botServerAppInsights?: BotServerAppInsights;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshTokens: () => void;
}

const devEnvironmentContext = {
  signIn: async () => {
    console.error('Sign in not implemented in dev environment');
    return;
  },
  signOut: async () => {
    console.error('Sign out not implemented in dev environment');
    return;
  },
  refreshTokens: () => {
    return;
  }
};

export const SignInContext = createContext<SignInContext>({
  user: undefined,
  signedInState: 'NotSignedIn',
  signIn: async () => {
    return;
  },
  signOut: async () => {
    return;
  },
  refreshTokens: () => {
    return;
  }
});

export interface SignInProviderProps {
  children: React.ReactNode;
}

export const SignInProvider = (props: SignInProviderProps): JSX.Element => {
  const { children } = props;

  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState<User | undefined>(undefined);
  const { instance, accounts, inProgress } = useMsal();

  const [cancellableFunc, cancelFunc] = useCancellableTask();

  const [seed, setSeed] = useState(Date.now());
  const refreshTokens = useCallback(() => {
    setSeed(Date.now());
  }, []);

  useEffect(() => {
    window.addEventListener('focus', refreshTokens);
    return () => window.removeEventListener('focus', refreshTokens);
  }, [refreshTokens]);

  useEffect(() => {
    if (isDevEnvironment) {
      setUser(devTestUser);
      return;
    }

    // This use effect has costly awaits such that a second use effect could be triggered before the first completes.
    // To prevent multiple appInsights instances being created for a single user, we use a cancellable task here.
    cancellableFunc(async (cancellable: Cancellable) => {
      if (isAuthenticated) {
        const graphToken = await authModule.getProfileAccessToken(instance, accounts);

        // functionsAppToken is only required in Torus env to call Function apis as they are hosted on Function App
        // secured by Microsoft auth.
        const functionsAppToken = await authModule.getAppAccessToken(instance, accounts);

        // Return early and do not create an app insights instance if this use effect has been triggered again.
        if (cancellable.cancelled) {
          return;
        }

        const name = accounts[0]?.name;
        const email = accounts[0]?.username;
        setUser({
          name: `${name ?? 'Unnamed Participant'}⭐`,
          email: email ?? 'email not found',
          graphToken,
          functionsAppToken
        });
      }
    });

    return () => {
      cancelFunc();
    };
  }, [isAuthenticated, instance, accounts, seed]);

  const appInsightsCreated = useRef(false);
  const [appInsights, setAppInsights] = useState<IApplicationInsights>();
  const [botServerAppInsights, setBotServerAppInsights] = useState<BotServerAppInsights>();
  const functionsAppToken = user?.functionsAppToken;
  useEffect(() => {
    if (appInsightsCreated.current === false && functionsAppToken) {
      // set ref first in case the use effect is fired multiple times during the async method.
      appInsightsCreated.current = true;

      // init default app insights tracking
      initLogger(functionsAppToken)
        .then((appInsights) => {
          appInsights.context.application.ver = `@azure/communication-calling: ${CALLING_SDK_VERSION}, @azure/communication-chat: ${CHAT_SDK_VERSION}, @azure/communication-react: ${COMMUNICATION_REACT_SDK_VERSION}`;
          setAppInsights(appInsights);
        })
        .catch((error) => {
          console.warn(
            'Failed to initialize default app insights. Telemetry will not be tracked. Error:',
            error.message
          );
        });

      // init bot server app insights tracking
      getBotServerAppInsightsConnectionString(functionsAppToken)
        .then((connectionString) => {
          setBotServerAppInsights((prev) => {
            prev?.dispose();
            return new BotServerAppInsights(connectionString);
          });
        })
        .catch((error) => {
          console.warn(
            'Failed to initialize speak to bot app insights. Telemetry will not be tracked. Error:',
            error.message
          );
        });
    }
  }, [functionsAppToken]);

  const signedInState: SignedInState =
    inProgress !== 'none' || (isAuthenticated && !user) ? 'CheckingState' : user ? 'SignedIn' : 'NotSignedIn';

  const signIn = useCallback(async (): Promise<void> => {
    await authModule.handleLogin(instance);
  }, [instance]);

  const signOut = useCallback(async (): Promise<void> => {
    clearLocalStorage();
    await authModule.handleLogout(instance);
  }, [instance]);

  const state: SignInContext = useMemo(
    () =>
      isDevEnvironment
        ? { ...devEnvironmentContext, user, signedInState, botServerAppInsights }
        : { user, signedInState, signIn, signOut, refreshTokens, appInsights, botServerAppInsights },
    [user, inProgress, signIn, signOut, refreshTokens, appInsights, botServerAppInsights]
  );

  return <SignInContext.Provider value={state}>{children}</SignInContext.Provider>;
};
